import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import Voltar from "@/components/Voltar";
import DuplicadoCnpjModal from "@/components/DuplicadoCnpjModal";
import { ArrowLeft, Loader2, CheckCircle2, AlertCircle } from "@/components/icons";
import { maskCNPJ, validCNPJ, maskCEP, validCEP, maskPhone, validPhone, digits } from "@/lib/formMasks";
import { fetchCNPJ, fetchCEP } from "@/lib/lookup";

const INPUT =
  "w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition";
const LABEL = "block text-sm font-medium text-slate-700 mb-1.5";

// Item 4.7 — Origem inicia em "Selecione…" e ganha "Prospecção em campo".
const ORIGENS = ["Website", "Indicação", "Instagram", "Ligação", "Prospecção", "Prospecção em campo", "Outros"];
// PEDIDO_EMERGENT_2A_UMA_PALAVRA: rótulos visíveis mudam, mas os valores no
// banco continuam Potencial/Ativo/Inativo (não mexer).
const STATUS = [
  { value: "Potencial", label: "Prospect" },
  { value: "Ativo", label: "Cliente" },
  { value: "Inativo", label: "Cliente inativo" },
];
const SEGMENTOS = [
  "Hospital / Saúde", "Farmacêutica", "Educação", "Indústria", "Indústria alimentícia",
  "Hotelaria", "Facilities / Terceirizadas", "Condomínios",
  "Alimentação / Restaurantes", "Buffet / Casa de festa", "Escritórios",
  "Igreja", "Academia", "Lavanderia", "Clubes", "Automotiva",
  "Órgão público", "Outros",
];

export default function ClienteForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [cnpjState, setCnpjState] = useState(null); // null | ok | invalid | dup
  const [cnpjDupInfo, setCnpjDupInfo] = useState(null);
  // LOTE X-1 F2
  const [dup, setDup] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [cnpjLookupBusy, setCnpjLookupBusy] = useState(false);
  const [cnpjSituacao, setCnpjSituacao] = useState(null); // {situacao, is_ativa}
  // LOTE CN-1 — mensagens claras ao lado do campo CNPJ.
  //   idle | busy | ok | invalid | not_found | situacao_alerta | unavailable
  // (o estado "dup" segue em `cnpjState` para preservar o aviso do X-1).
  const [cnpjMsg, setCnpjMsg] = useState("idle");
  const [cnpjSource, setCnpjSource] = useState(null);
  const [cepBusy, setCepBusy] = useState(false);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  const [cepInvalid, setCepInvalid] = useState(false);
  const [form, setForm] = useState({
    nome: "", nome_fantasia: "", decisor_nome: "", decisor_cargo: "", email: "", telefone: "", cnpj: "", cep: "", logradouro: "", numero: "",
    complemento: "", bairro: "", cidade: "", estado: "", origem: "", segmento: "",
    ja_cliente_mpires: false, status: "Potencial", notas: "",
  });

  useEffect(() => {
    if (editing) {
      api.get(`/clientes/${id}`).then((r) => {
        setForm((f) => ({ ...f, ...Object.fromEntries(Object.keys(f).map((k) => [k, r.data[k] ?? f[k]])) }));
      }).catch(() => toast.error("Cliente não encontrado")).finally(() => setLoading(false));
    }
  }, [id, editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Item 4.6 — máscaras controladas para CNPJ/CEP/Telefone
  const onCnpjChange = (e) => {
    const masked = maskCNPJ(e.target.value);
    setForm((f) => ({ ...f, cnpj: masked }));
  };
  const onCepChange = async (e) => {
    const masked = maskCEP(e.target.value);
    setForm((f) => ({ ...f, cep: masked }));
    setCepInvalid(false);
    if (digits(masked).length === 8) {
      // Item 4.5 — ViaCEP preenche endereço.
      setCepBusy(true);
      const via = await fetchCEP(masked);
      setCepBusy(false);
      if (via) {
        setForm((f) => ({
          ...f,
          logradouro: via.logradouro || f.logradouro,
          bairro: via.bairro || f.bairro,
          cidade: via.cidade || f.cidade,
          estado: via.estado || f.estado,
        }));
      }
    }
  };
  const onCepBlur = () => {
    if (form.cep && !validCEP(form.cep)) setCepInvalid(true);
  };
  const onPhoneChange = (e) => {
    setForm((f) => ({ ...f, telefone: maskPhone(e.target.value) }));
    setPhoneInvalid(false);
  };
  const onPhoneBlur = () => {
    if (form.telefone && !validPhone(form.telefone)) setPhoneInvalid(true);
  };

  // Item 4.4 — CNPJ preenche o cadastro (consulta backend); item 4.6 valida DV.
  // LOTE CN-1 — cadeia BrasilAPI → cnpj.ws → ReceitaWS + cache + mensagens claras.
  const onCnpjBlur = async () => {
    if (!form.cnpj) {
      setCnpjState(null); setCnpjSituacao(null); setCnpjMsg("idle"); setCnpjSource(null); return;
    }
    if (!validCNPJ(form.cnpj)) {
      setCnpjState("invalid"); setCnpjSituacao(null); setCnpjMsg("invalid"); setCnpjSource(null); return;
    }
    setCnpjState(null);
    setCnpjMsg("busy");
    setCnpjSource(null);
    setCnpjLookupBusy(true);
    const [bapi, verify] = await Promise.all([
      fetchCNPJ(form.cnpj),
      api.get("/clientes/verificar", { params: { cnpj: form.cnpj, excluir_id: id } })
        .then((r) => r.data).catch(() => null),
    ]);
    setCnpjLookupBusy(false);
    // Duplicado (X-1) tem prioridade visual — aviso continua igual.
    if (verify?.duplicado) {
      setCnpjState("dup");
      setCnpjDupInfo((verify.duplicados || [])[0] || null);
      setDup(verify?.dup || null);
      if (verify?.dup) setDupOpen(true);
    } else {
      setCnpjState("ok"); setCnpjDupInfo(null); setDup(null);
    }
    const st = bapi?.status || "unavailable";
    if (st === "ok" || st === "situacao_alerta") {
      const d = bapi.data || {};
      setCnpjSituacao({ situacao: bapi.situacao, is_ativa: st === "ok" });
      setCnpjSource(bapi.source || null);
      setCnpjMsg(verify?.duplicado ? "dup" : (st === "situacao_alerta" ? "situacao_alerta" : "ok"));
      setForm((f) => ({
        ...f,
        nome: f.nome || d.empresa || "",
        // LOTE NF-1 — preenche nome fantasia só se o vendedor ainda não digitou.
        nome_fantasia: f.nome_fantasia || d.nome_fantasia || "",
        cep: f.cep || (d.cep ? maskCEP(d.cep) : f.cep),
        logradouro: f.logradouro || d.logradouro || "",
        numero: f.numero || d.numero || "",
        complemento: f.complemento || d.complemento || "",
        bairro: f.bairro || d.bairro || "",
        cidade: f.cidade || d.cidade || "",
        estado: f.estado || d.estado || "",
        segmento: f.segmento || d.segmento_sugerido || "",
      }));
    } else if (st === "not_found") {
      setCnpjSituacao(null); setCnpjMsg(verify?.duplicado ? "dup" : "not_found");
    } else if (st === "invalid") {
      setCnpjState("invalid"); setCnpjSituacao(null); setCnpjMsg("invalid");
    } else {
      setCnpjSituacao(null); setCnpjMsg(verify?.duplicado ? "dup" : "unavailable");
    }
  };

  const validateEmail = (v) => {
    const t = (v || "").trim();
    if (!t) { setEmailInvalid(false); return true; }
    const ok = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(t);
    setEmailInvalid(!ok);
    return ok;
  };

  const daysSince = (iso) => {
    if (!iso) return null;
    try { return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)); }
    catch { return null; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (cnpjState === "invalid") { toast.error("CNPJ inválido"); return; }
    if (cnpjState === "dup") { toast.error("CNPJ já cadastrado — verifique o responsável atual"); return; }
    if (!validateEmail(form.email)) { toast.error("E-mail em formato inválido"); return; }
    setSaving(true);
    try {
      // LOTE CN-1 — grava `cnpj_situacao` apenas quando a Receita retornou
      // alerta (BAIXADA/INAPTA/SUSPENSA). "ATIVA" ou vazio não é enviado.
      const payload = { ...form };
      if (cnpjSituacao && !cnpjSituacao.is_ativa && cnpjSituacao.situacao) {
        payload.cnpj_situacao = cnpjSituacao.situacao;
      }
      if (editing) {
        await api.put(`/clientes/${id}`, payload);
        toast.success("Cliente atualizado");
        navigate(`/cliente/${id}`);
      } else {
        const { data } = await api.post("/clientes", payload);
        toast.success("Cliente cadastrado");
        navigate(`/cliente/${data.id}`);
      }
    } catch (err) {
      toast.error(errMsg(err, "Não foi possível salvar o cliente"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="max-w-3xl">
      <Voltar fallback="/clientes" className="mb-4" />
      <PageHeader title={editing ? "Editar cliente" : "Novo prospect"} subtitle="O CNPJ é obrigatório e validado contra toda a base da empresa." />

      <form onSubmit={submit} className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] space-y-5" data-testid="cliente-form">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={LABEL}>Empresa / Razão social *</label>
            <input required value={form.nome} onChange={set("nome")} className={INPUT} data-testid="cli-nome" placeholder="Ex: Prátika Facilities Hospitalar" />
            <p className="text-[11px] text-muted-foreground mt-1">Nome da empresa cliente — é o que aparece em destaque na carteira e no funil.</p>
          </div>
          <div className="md:col-span-2">
            <label className={LABEL}>Nome fantasia (opcional)</label>
            <input value={form.nome_fantasia} onChange={set("nome_fantasia")} className={INPUT} data-testid="cli-nome-fantasia" placeholder="Como o cliente é conhecido no dia a dia" />
            <p className="text-[11px] text-muted-foreground mt-1">Se consultarmos o CNPJ, preenchemos automaticamente. Nunca sobrescrevemos o que você digitou.</p>
          </div>
          <div>
            <label className={LABEL}>Contato principal (opcional)</label>
            <input value={form.decisor_nome} onChange={set("decisor_nome")} className={INPUT} data-testid="cli-decisor-nome" placeholder="Ex: Maria Silva" />
          </div>
          <div>
            <label className={LABEL}>Cargo do contato</label>
            <input value={form.decisor_cargo} onChange={set("decisor_cargo")} className={INPUT} placeholder="Ex.: Diretor de Compras" data-testid="cli-cargo-contato" />
          </div>
          <div>
            <label className={LABEL}>CNPJ *</label>
            <div className="relative">
              <input required value={form.cnpj} onChange={onCnpjChange} onBlur={onCnpjBlur} className={INPUT} placeholder="00.000.000/0000-00" data-testid="cli-cnpj" inputMode="numeric" maxLength={18} />
              {cnpjMsg === "busy" && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1" data-testid="cli-cnpj-busy"><Loader2 className="w-3 h-3 animate-spin" /> Consultando a Receita…</p>}
              {cnpjMsg === "ok" && (
                <p className="text-xs text-ok mt-1 flex items-center gap-1" data-testid="cli-cnpj-ok">
                  <CheckCircle2 className="w-3 h-3" /> Dados preenchidos pela Receita Federal{cnpjSource ? ` (${cnpjSource})` : ""}.
                </p>
              )}
              {cnpjMsg === "not_found" && (
                <p className="text-xs text-warn mt-1" data-testid="cli-cnpj-notfound">CNPJ não encontrado na Receita.</p>
              )}
              {cnpjMsg === "situacao_alerta" && cnpjSituacao && (
                <p className="text-xs text-warn mt-1" data-testid="cli-cnpj-situacao">⚠️ Atenção: CNPJ com situação <b>{cnpjSituacao.situacao}</b> na Receita.</p>
              )}
              {cnpjMsg === "unavailable" && (
                <div className="mt-1 flex items-center gap-2 text-xs text-warn" data-testid="cli-cnpj-unavailable">
                  <AlertCircle className="w-3 h-3" />
                  <span>Serviço da Receita indisponível agora. Preencha manualmente ou tente de novo.</span>
                  <button type="button" onClick={onCnpjBlur} data-testid="cli-cnpj-retry"
                    className="font-semibold text-brand hover:underline">Tentar de novo</button>
                </div>
              )}
              {cnpjState === "invalid" && cnpjMsg !== "unavailable" && <p className="text-xs text-bad mt-1" data-testid="cli-cnpj-invalid">CNPJ inválido (dígito verificador não confere).</p>}
              {cnpjState === "ok" && cnpjMsg === "ok" && <CheckCircle2 className="absolute right-3 top-[22px] -translate-y-1/2 w-4 h-4 text-ok" />}
              {(cnpjState === "invalid" || cnpjState === "dup") && <AlertCircle className="absolute right-3 top-[22px] -translate-y-1/2 w-4 h-4 text-bad" />}
            </div>
            {cnpjState === "dup" && (
              <div className="mt-2 rounded-lg border border-bad/30 bg-bad-soft/40 p-3 text-xs" data-testid="cli-cnpj-dup">
                <div className="font-semibold text-bad mb-1">Este CNPJ já está cadastrado no MPires Connect.</div>
                <div className="space-y-0.5 text-navy">
                  {cnpjDupInfo?.cnpj_formatado && (
                    <div><span className="text-muted-foreground">CNPJ:</span> <b>{cnpjDupInfo.cnpj_formatado}</b></div>
                  )}
                  {cnpjDupInfo?.nome && (
                    <div><span className="text-muted-foreground">Nome no MPires Connect:</span> <b>{cnpjDupInfo.nome}</b></div>
                  )}
                  {cnpjDupInfo?.owner_name && (
                    <div><span className="text-muted-foreground">Responsável:</span> <b>{cnpjDupInfo.owner_name}</b></div>
                  )}
                  {cnpjDupInfo?.manager_name && (
                    <div><span className="text-muted-foreground">Equipe:</span> <b>{cnpjDupInfo.manager_name}</b></div>
                  )}
                  {cnpjDupInfo?.status && (
                    <div><span className="text-muted-foreground">Situação:</span> <b>{cnpjDupInfo.efetivado ? "Cliente efetivado" : (String(cnpjDupInfo.status).toLowerCase() === "potencial" ? "Prospect" : cnpjDupInfo.status)}</b></div>
                  )}
                  {!cnpjDupInfo?.efetivado && cnpjDupInfo?.expira_em_br && (
                    <div><span className="text-muted-foreground">Exclusividade:</span> até <b>{cnpjDupInfo.expira_em_br}</b></div>
                  )}
                </div>
                {dup && (dup.pode_ampliar_mix || dup.pode_abrir_ficha || dup.pode_avisar_responsavel) && (
                  <button type="button" onClick={() => setDupOpen(true)}
                    data-testid="cli-cnpj-dup-abrir"
                    className="mt-2 text-xs font-semibold text-brand hover:underline">
                    {dup.pode_ampliar_mix ? "Ampliar mix com este cliente" :
                     dup.pode_abrir_ficha ? "Abrir ficha do cliente" :
                     "Avisar o responsável"}
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            <label className={LABEL}>Telefone</label>
            <input value={form.telefone} onChange={onPhoneChange} onBlur={onPhoneBlur} className={INPUT} placeholder="(11) 99999-9999" data-testid="cli-telefone" inputMode="tel" maxLength={16} />
            {phoneInvalid && <p className="text-xs text-bad mt-1" data-testid="cli-telefone-invalid">Telefone precisa de DDD e ao menos 10 dígitos.</p>}
          </div>
          <div>
            <label className={LABEL}>E-mail</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); if (emailInvalid) validateEmail(e.target.value); }}
              onBlur={(e) => validateEmail(e.target.value)}
              className={`${INPUT} ${emailInvalid ? "border-bad focus:ring-bad/30" : ""}`}
              data-testid="cli-email"
            />
            {emailInvalid && <p className="text-xs text-bad mt-1" data-testid="cli-email-erro">E-mail em formato inválido (use nome@dominio.tld)</p>}
          </div>
          <div>
            <label className={LABEL}>CEP</label>
            <input value={form.cep} onChange={onCepChange} onBlur={onCepBlur} className={INPUT} placeholder="00000-000" data-testid="cli-cep" inputMode="numeric" maxLength={9} />
            {cepBusy && <p className="text-xs text-muted-foreground mt-1"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Buscando CEP…</p>}
            {cepInvalid && <p className="text-xs text-bad mt-1" data-testid="cli-cep-invalid">CEP precisa ter 8 dígitos.</p>}
          </div>
          <div className="md:col-span-2 grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className={LABEL}>Logradouro</label>
              <input value={form.logradouro} onChange={set("logradouro")} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Número</label>
              <input value={form.numero} onChange={set("numero")} className={INPUT} inputMode="numeric" />
            </div>
          </div>
          <div>
            <label className={LABEL}>Complemento</label>
            <input value={form.complemento} onChange={set("complemento")} className={INPUT} placeholder="Sala, bloco, galpão…" data-testid="cli-complemento" />
          </div>
          <div>
            <label className={LABEL}>Bairro</label>
            <input value={form.bairro} onChange={set("bairro")} className={INPUT} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className={LABEL}>Cidade</label>
              <input value={form.cidade} onChange={set("cidade")} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>UF</label>
              <input maxLength={2} value={form.estado} onChange={set("estado")} className={INPUT} />
            </div>
          </div>
          <div>
            <label className={LABEL}>Origem</label>
            <select value={form.origem} onChange={set("origem")} className={INPUT} data-testid="cli-origem">
              <option value="">Selecione…</option>
              {ORIGENS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Segmento {!editing && "*"}</label>
            <select required={!editing} value={form.segmento || ""} onChange={set("segmento")} className={INPUT} data-testid="cli-segmento">
              <option value="">{editing ? "Não informado" : "Selecione…"}</option>
              {SEGMENTOS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Situação</label>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value, ja_cliente_mpires: e.target.value === "Ativo" }))} className={INPUT} data-testid="cli-status">
              {STATUS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={LABEL}>Notas</label>
          <textarea value={form.notas} onChange={set("notas")} rows={3} className={`${INPUT} h-auto py-2.5`} />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate(-1)} className="h-11 px-5 rounded-lg border border-input text-sm font-medium hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="save-cliente-btn"
            className="h-11 px-6 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Salvar alterações" : "Cadastrar prospect"}
          </button>
        </div>
      </form>
      <DuplicadoCnpjModal
        open={dupOpen && !!dup}
        onOpenChange={setDupOpen}
        dup={dup}
        cnpjConsultado={form.cnpj}
        razaoConsultada={form.nome}
      />
    </div>
  );
}
